/**
 * filesService — minimal client SDK for files-service.
 *
 * Same model as photosService.js / groupsService.js: same-origin fetch
 * through /files-api/* which is proxied by integration-gateway with a
 * server-side JWT. Browser only carries the genealorama_session cookie.
 *
 * Override the base path with window.__FILES_API_BASE__ for testing.
 */

const FILES_API_BASE =
	(typeof window !== "undefined" && window.__FILES_API_BASE__) ||
	"/files-api";

class FilesService {
	/**
	 * GET /files-api/files
	 * @param {{kind?: string, limit?: number, cursor?: string}} opts
	 * @returns {Promise<{files: Array, nextCursor: string|null}>}
	 */
	async listFiles({ kind, limit, cursor, includeSuperseded } = {}) {
		const params = new URLSearchParams();
		if (kind) params.set("kind", kind);
		if (limit != null) params.set("limit", String(limit));
		if (cursor) params.set("cursor", cursor);
		if (includeSuperseded) params.set("include_superseded", "true");
		const qs = params.toString();
		return this._request(`/files${qs ? `?${qs}` : ""}`);
	}

	/**
	 * GET /files-api/files/:id — metadata + signed download URL (1h TTL).
	 */
	async getFile(id) {
		const res = await this._rawRequest(`/files/${encodeURIComponent(id)}`);
		if (res.status === 404) return null;
		const data = await res.json().catch(() => ({}));
		if (!res.ok) throw new Error(data.error || `files-api ${res.status}`);
		return data;
	}

	/**
	 * POST /files-api/files (multipart). Accepts a single File, an
	 * optional kind ('gedcom' by default in geneafan), and an optional
	 * familyName which becomes metadata.familyName + the primary group
	 * name (much cleaner than parsing it back out of the filename).
	 *
	 * Returns the uploaded entry. On dedupe hit, returns the existing row
	 * (status === 'duplicate').
	 */
	async uploadFile(file, { kind = "gedcom", familyName = null } = {}) {
		const form = new FormData();
		form.append("files", file, file.name);
		if (kind) form.append("kind", kind);
		if (familyName) form.append("family_name", familyName);
		const data = await this._request("/files", { method: "POST", body: form });
		return Array.isArray(data?.uploaded) ? data.uploaded[0] : null;
	}

	/**
	 * POST /files-api/files/:id/share
	 *   body: { emails: string[], message?: string }
	 *   returns: { fileId, validationResults, accountResults, sharingResults,
	 *              invitationResults, mailchimpResults }
	 *
	 * Pipeline (handled server-side in files-service):
	 *   1. validate deliverability via email-validator
	 *   2. mint guest registration tokens via auth-service
	 *   3. grant group membership via groups-service
	 *   4. send invitation email via emails-service
	 *   5. tag in Mailchimp (best-effort)
	 *
	 */
	async shareFile(id, { emails, message } = {}) {
		return this._request(`/files/${encodeURIComponent(id)}/share`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ emails, message }),
		});
	}

	/**
	 * PUT /files-api/files/:id/content — remplacement in-place du contenu
	 * (réservé au propriétaire ; 404 pour un invité). Même id → groupe,
	 * photos, partages et mergeHistory conservés. Utilisé par l'auto-save
	 * des enrichissements (.geneatree vivant).
	 * @param {string} id
	 * @param {string|Blob} content
	 * @param {string} [contentType]
	 */
	async updateFileContent(id, content, contentType = "text/plain") {
		return this._request(`/files/${encodeURIComponent(id)}/content`, {
			method: "PUT",
			headers: { "Content-Type": contentType },
			body: content,
		});
	}

	async deleteFile(id) {
		const res = await this._rawRequest(`/files/${encodeURIComponent(id)}`, { method: "DELETE" });
		if (res.status === 404) return false;
		if (!res.ok) {
			const data = await res.json().catch(() => ({}));
			throw new Error(data.error || `files-api delete ${res.status}`);
		}
		return true;
	}

	// === Reciprocal merge proposals (guest-initiated cross-user merges) ===
	// All endpoints documented in project_guest_initiated_proposal_flow.md.

	async submitProposal({ contributorFileId, recipientEmail, message }) {
		return this._request("/proposals", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ contributorFileId, recipientEmail, message }),
		});
	}

	async listProposalInbox({ limit } = {}) {
		const qs = limit != null ? `?limit=${encodeURIComponent(limit)}` : "";
		return this._request(`/proposals/inbox${qs}`);
	}

	async listProposalOutbox({ status, limit } = {}) {
		const params = new URLSearchParams();
		if (status) params.set("status", status);
		if (limit != null) params.set("limit", String(limit));
		const qs = params.toString();
		return this._request(`/proposals/outbox${qs ? `?${qs}` : ""}`);
	}

	async rejectProposal(id) {
		return this._request(`/proposals/${encodeURIComponent(id)}/reject`, { method: "POST" });
	}

	async completeProposalRecipient(id, { recipientFileId, resultingFileId }) {
		return this._request(`/proposals/${encodeURIComponent(id)}/complete-recipient`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ recipientFileId, resultingFileId }),
		});
	}

	async completeProposalContributor(id, { resultingFileId }) {
		return this._request(`/proposals/${encodeURIComponent(id)}/complete-contributor`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ resultingFileId }),
		});
	}

	async abandonProposalContributor(id) {
		return this._request(`/proposals/${encodeURIComponent(id)}/abandon-contributor`, { method: "POST" });
	}

	async _rawRequest(path, init = {}) {
		return fetch(`${FILES_API_BASE}${path}`, {
			...init,
			credentials: "same-origin",
		});
	}

	async _request(path, init = {}) {
		const res = await this._rawRequest(path, init);
		const data = await res.json().catch(() => ({}));
		if (!res.ok) {
			throw new Error(data.error || `files-api ${path} ${res.status}`);
		}
		return data;
	}
}

export const filesService = new FilesService();

if (typeof window !== "undefined") {
	window.__filesService = filesService;
}
